// src/lib/slotConfig.js
//
// Canonical field registry — single source of truth for field keys,
// option values, labels, hints, and storage targets.
//
// Rules:
//   - Option VALUES are sacred. Never change them — they're stored in the DB.
//   - Option LABELS are copy. Change them freely.
//   - storage: 'stable'  → tax_profiles
//   - storage: 'year'    → tax_year_profiles
//   - storage: 'subject' → tax_subjects
//   - storage: 'session' → triage_sessions only (not persisted to profile)
//
// Usage:
//   import { slots } from '../lib/slotConfig'
//   // Use slots.income_types.options in your form config
//   // Override labels at the form level — don't change canonical values here
//
// TODO: Migrate ProfilePage.js to consume this registry section by section.
//       See BACKLOG.md for sequencing notes.

export const slots = {

  // ─── Identity ──────────────────────────────────────────────────────────────

  citizenship_status: {
    key:        'citizenship_status',
    storage:    'stable',
    type:       'single_select',
    label:      'What is your citizenship status?',
    shortLabel: 'Citizenship',
    hint:       'Determines whether US filing obligations apply.',
    options: [
      { value: 'us_citizen',  label: '🇺🇸 US citizen (including dual citizens)' },
      { value: 'green_card',  label: '🇺🇸 US Green Card holder' },
      { value: 'eu_citizen',  label: '🇪🇺 EU citizen (non-US)' },
      { value: 'other',       label: 'Other nationality' },
    ],
  },

  eu_citizen: {
    key:        'eu_citizen',
    storage:    'stable',
    type:       'boolean',
    label:      'Do you also hold US citizenship or a Green Card?',
    shortLabel: 'Also US person',
    hint:       'Some people hold both EU and US citizenship.',
    options: [
      { value: true,  label: 'Yes — I also have US citizenship or a Green Card', reportLabel: 'US citizenship / Green Card also held' },
      { value: false, label: 'No — EU citizenship only', reportLabel: 'EU citizenship only' },
    ],
  },

  comunidad_autonoma: {
    key:        'comunidad_autonoma',
    storage:    'stable',
    type:       'single_select',
    label:      'Which region of Spain do you live in?',
    shortLabel: 'Comunidad Autónoma',
    hint:       'Regional rules affect wealth tax, inheritance tax, and certain deductions.',
    options: [
      { value: 'andalucia',          label: 'Andalucía' },
      { value: 'aragon',             label: 'Aragón' },
      { value: 'asturias',           label: 'Asturias' },
      { value: 'baleares',           label: 'Illes Balears' },
      { value: 'canarias',           label: 'Canarias' },
      { value: 'cantabria',          label: 'Cantabria' },
      { value: 'castilla_la_mancha', label: 'Castilla-La Mancha' },
      { value: 'castilla_leon',      label: 'Castilla y León' },
      { value: 'cataluna',           label: 'Catalunya' },
      { value: 'extremadura',        label: 'Extremadura' },
      { value: 'galicia',            label: 'Galicia' },
      { value: 'la_rioja',           label: 'La Rioja' },
      { value: 'madrid',             label: 'Comunidad de Madrid' },
      { value: 'murcia',             label: 'Región de Murcia' },
      { value: 'navarra',            label: 'Navarra' },
      { value: 'pais_vasco',         label: 'País Vasco' },
      { value: 'valencia',           label: 'Comunitat Valenciana' },
    ],
  },

  marital_status: {
    key:        'marital_status',
    storage:    'stable',
    type:       'single_select',
    label:      'What is your marital status?',
    shortLabel: 'Marital status',
    hint:       'Affects wealth tax calculations and certain deductions.',
    options: [
      { value: 'married',            label: 'Married' },
      { value: 'civil_partnership',  label: 'Civil partnership (pareja de hecho)' },
      { value: 'single',             label: 'Single' },
      { value: 'separated_divorced', label: 'Separated or divorced' },
      { value: 'widowed',            label: 'Widowed' },
    ],
  },

  asset_split: {
    key:        'asset_split',
    storage:    'stable',
    type:       'single_select',
    label:      'How are your assets broadly split between you and your partner?',
    shortLabel: 'Asset split',
    hint:       'Wealth tax is assessed individually. The split affects whether thresholds apply to you.',
    options: [
      { value: 'roughly_equal',   label: 'Roughly equal between both', reportLabel: 'Roughly equal' },
      { value: 'one_spouse_more', label: 'One spouse holds significantly more', reportLabel: 'One spouse holds more' },
      { value: 'all_one_spouse',  label: "Almost all in one spouse's name", reportLabel: "Almost all one spouse" },
      { value: 'not_sure',        label: "Not sure how assets are titled", reportLabel: 'Not confirmed' },
    ],
  },

  beckham_status: {
    key:        'beckham_status',
    storage:    'stable',
    type:       'single_select',
    label:      'Are you enrolled in the Beckham Law special regime?',
    shortLabel: 'Beckham Law status',
    hint:       'Beckham Law uses a different tax regime than ordinary IRPF.',
    // Full parity with the calculator's design: clean Yes/No as the only
    // stored values, with a "Not sure?" toggle that reveals this text
    // inline rather than a third stored option. A third value was tried in
    // triage before (considering/applied/not_eligible/unknown) — dropped
    // because none of it changed the actual flag logic beyond "active or
    // not," so the extra granularity wasn't earning its complexity.
    helpText:   "Beckham Law isn't automatic — it requires an application that was approved by the Agencia Tributaria. If you never applied, or aren't sure whether your application was approved, the answer is almost always No.",
    options: [
      { value: 'not_active', label: 'No' },
      { value: 'active',     label: 'Yes' },
    ],
  },

  // ─── Residency ─────────────────────────────────────────────────────────────

  residency_year_type: {
    key:        'residency_year_type',
    storage:    'year',
    type:       'single_select',
    label:      'Where did you live during this tax year?',
    shortLabel: 'Residency type',
    hint:       null,
    options: [
      { value: 'full_year_spain', label: '🇪🇸 Spain only — full year', reportLabel: '🇪🇸 Full year in Spain' },
      { value: 'full_year_us',    label: '🇺🇸 US only — full year', reportLabel: '🇺🇸 Full year in US' },
      { value: 'split_year',      label: '🇺🇸 + 🇪🇸 Both — moved during the year', reportLabel: '🇺🇸 + 🇪🇸 Arrival / split year' },
    ],
  },

  spain_residency_status: {
    key:        'spain_residency_status',
    storage:    'year',
    type:       'single_select',
    label:      'What is your Spanish tax residency status for this year?',
    shortLabel: 'Spain residency',
    hint:       null,
    options: [
      { value: 'resident_full_year',    label: '🇪🇸 Resident — full year', reportLabel: '🇪🇸 Full-year Spanish resident' },
      { value: 'resident_partial_year', label: '🇪🇸 Resident — part of year', reportLabel: '🇪🇸 Partial-year Spanish resident' },
      { value: 'non_resident',          label: 'Not a resident this year', reportLabel: 'Non-resident' },
      { value: 'uncertain',             label: 'Not sure', reportLabel: 'Uncertain' },
    ],
  },

  // ─── Income ────────────────────────────────────────────────────────────────

  income_types: {
    key:              'income_types',
    storage:          'year',
    type:             'multi_select',
    label:            'What types of income did you have that year?',
    shortLabel:       'Income types',
    hint:             'Select all that apply.',
    exclusiveOptions: ['none'],
    options: [
      { value: 'employment_es',        label: '🇪🇸 Spanish employment salary' },
      { value: 'employment_us',        label: '🇺🇸 US employment (W-2 or salary)', reportLabel: '🇺🇸 US employment' },
      { value: 'self_employment',      label: 'Self-employment' },
      // NEW — closes the biggest gap vs. the calculator: K-1/S-corp and foreign
      // company distributions are the product's own headline classification-uncertainty
      // category. Triage previously had no way to acknowledge this income exists at all.
      { value: 'business_partnership', label: 'Business or partnership income (K-1, S-corp, LLC, foreign company)', reportLabel: 'Business / partnership income' },
      { value: 'rental',               label: 'Rental income' },
      { value: 'investments',          label: 'Investment income (dividends, interest, capital gains)', reportLabel: 'Investment income' },
      { value: 'pension',              label: 'Pension or retirement income' },
      { value: 'none',                 label: 'None of the above', reportLabel: 'None' },
    ],
  },

  // NEW — inline sub-question shown when 'business_partnership' is selected in income_types.
  business_partnership_type: {
    key:        'business_partnership_type',
    storage:    'year',
    type:       'single_select',
    label:      'Is this from a US pass-through entity, or a foreign company?',
    shortLabel: 'Business income type',
    hint:       "US LLC/S-corp classification in Spain is genuinely unsettled — this helps us flag it correctly.",
    options: [
      { value: 'us_pass_through', label: 'US LLC or S-corp (K-1 income)', reportLabel: 'US LLC / S-corp (K-1)' },
      { value: 'foreign_company', label: 'Foreign company distribution',  reportLabel: 'Foreign company distribution' },
      { value: 'not_sure',        label: 'Not sure',                      reportLabel: 'Business/partnership type unclear' },
    ],
  },

  self_employment_country: {
    key:        'self_employment_country',
    storage:    'year',
    type:       'single_select',
    label:      'Where is your self-employment activity based?',
    shortLabel: 'Self-employment location',
    hint:       'Determines autónomo obligations and Social Security / totalization treatment.',
    options: [
      { value: 'spain', label: '🇪🇸 Spain (autónomo)', reportLabel: '🇪🇸 Spain' },
      { value: 'us',    label: '🇺🇸 United States' },
      { value: 'both',  label: '🇺🇸 + 🇪🇸 Both' },
      { value: 'other', label: '🌍 Another country' },
    ],
  },

  employment_location_when_earned: {
    key:        'employment_location_when_earned',
    storage:    'year',
    type:       'single_select',
    label:      'Where was this employment income earned?',
    shortLabel: 'Employment location',
    hint:       'This affects which country has primary taxing rights and whether FEIE or FTC applies.',
    options: [
      { value: 'us_only',          label: '🇺🇸 Earned while physically in the US' },
      { value: 'spain_only',       label: '🇪🇸 Earned while physically in Spain' },
      { value: 'both_us_spain',    label: '🇺🇸 + 🇪🇸 Split between US and Spain' },
      { value: 'other_or_unclear', label: 'Other / unclear' },
    ],
  },

  rental_property_location: {
    key:        'rental_property_location',
    storage:    'year',
    type:       'single_select',
    label:      'Where is the rental property located?',
    shortLabel: 'Rental location',
    hint:       "Determines which country\'s rules apply to the rental income.",
    options: [
      { value: 'spain', label: '🇪🇸 Spain only' },
      { value: 'us',    label: '🇺🇸 US only' },
      { value: 'both',  label: '🇺🇸 + 🇪🇸 Both' },
      { value: 'other', label: '🌍 Another country' },
    ],
  },

  pension_source: {
    key:        'pension_source',
    storage:    'year',
    type:       'single_select',
    label:      'Where is your pension or retirement income from?',
    shortLabel: 'Pension source',
    hint:       'US and Spanish pensions have different treaty treatment in Spain.',
    options: [
      { value: 'spanish', label: '🇪🇸 Spanish pension or retirement plan' },
      { value: 'us',      label: '🇺🇸 US pension, 401(k), IRA, or Social Security' },
      { value: 'both',    label: '🇺🇸 + 🇪🇸 Both' },
      { value: 'other',   label: '🌍 Another country' },
    ],
  },

  // NEW — inline sub-question shown when pension_source is 'us', 'both', or 'other'
  // (i.e. any non-Spanish-only pension). Government/civil-service pensions typically
  // have different treaty sourcing than private pensions or Social Security.
  pension_is_government: {
    key:        'pension_is_government',
    storage:    'year',
    type:       'boolean',
    label:      'Is this a government or civil-service pension (federal, state, or military), or private-sector?',
    shortLabel: 'Government pension',
    hint:       'Government pensions often have different treaty sourcing rules than private pensions.',
    options: [
      { value: true,  label: 'Government / civil-service / military', reportLabel: 'Government pension' },
      { value: false, label: 'Private-sector', reportLabel: 'Private pension' },
    ],
  },

  // ─── Accounts & assets ─────────────────────────────────────────────────────

  foreign_account_band: {
    key:        'foreign_account_band',
    storage:    'year',
    type:       'single_select',
    label: 'How much did you have in financial accounts outside Spain?',
    shortLabel: 'Foreign account band',
    hint:       'Include bank accounts, brokerages, retirement accounts, and any other non-Spanish accounts. Use the peak value at any point during the year.',
    options: [
      { value: 'none',         label: "I don't have accounts outside Spain", reportLabel: 'None reported' },
      { value: 'under_10k',    label: 'Under $10,000 / €10,000' },
      { value: 'band_10_50k',  label: '$10,000 – $50,000' },
      { value: 'band_50_200k', label: '$50,000 – $200,000' },
      { value: 'band_200k_1m', label: '$200,000 – $1,000,000' },
      { value: 'over_1m',      label: 'Over $1,000,000' },
    ],
  },

  worldwide_net_worth_band: {
    key:        'worldwide_net_worth_band',
    storage:    'year',
    type:       'single_select',
    label:      'Roughly what was your worldwide net worth at the end of that tax year?',
    shortLabel: 'Net worth band',
    hint:       'Include property, investments, bank accounts, retirement accounts, and business interests, minus debts. A rough range is fine.',
    options: [
      { value: 'under_300k',    label: 'Under €300,000' },
      { value: 'band_300_700k', label: '€300,000 – €700,000' },
      { value: 'band_700k_1m',  label: '€700,000 – €1,000,000' },
      { value: 'band_1m_2m',    label: '€1,000,000 – €2,000,000' },
      { value: 'band_2m_3m',    label: '€2,000,000 – €3,000,000' },
      { value: 'over_3m',       label: 'Over €3,000,000' },
      { value: 'not_sure',      label: 'Not sure / prefer not to say', reportLabel: 'Not provided' },
    ],
  },

  // ─── Property ──────────────────────────────────────────────────────────────

  property_ownership: {
    key:              'property_ownership',
    storage:          'year',
    type:             'multi_select',
    label:            'Did you own any real property during that year?',
    shortLabel:       'Property ownership',
    hint:             'Select all that apply.',
    exclusiveOptions: ['none'],
    options: [
      { value: 'spain_property', label: '🇪🇸 Property in Spain' },
      { value: 'us_property',    label: '🇺🇸 Property in the US' },
      { value: 'other_property', label: '🌍 Property in another country' },
      { value: 'none',           label: 'No property owned' },
    ],
  },

  property_sale_location: {
    key:        'property_sale_location',
    storage:    'year',
    type:       'single_select',
    label:      'Where was the property you sold located?',
    shortLabel: 'Property sale location',
    hint:       "Determines which country\'s capital gains rules apply.",
    options: [
      { value: 'spain',         label: '🇪🇸 Spain' },
      { value: 'us',            label: '🇺🇸 United States' },
      { value: 'other_foreign', label: '🌍 Another country' },
    ],
  },

  // ─── Major events ──────────────────────────────────────────────────────────
  //
  // moved_countries, bought_property, and started_business removed —
  // audited against deriveFacts.ts + runTriageFlags.ts and confirmed none
  // of the three ever drove a flag. moved_countries was also fully redundant
  // with move_date, which already drives moveIsThisYear / split_year_residency.

  major_events: {
    key:              'major_events',
    storage:          'year',
    type:             'multi_select',
    label:            'Did any of these happen during that tax year?',
    shortLabel:       'Major events',
    hint:             'Select all that apply.',
    exclusiveOptions: ['none'],
    options: [
      { value: 'sold_property',         label: 'Sold a property' },
      { value: 'retirement_withdrawal', label: 'Took a retirement account withdrawal', reportLabel: 'Retirement withdrawal' },
      { value: 'large_investment_sale', label: 'Sold a large investment or portfolio position', reportLabel: 'Large investment sale' },
      { value: 'inheritance_or_gift',   label: 'Received an inheritance or significant gift', reportLabel: 'Inheritance or gift received' },
      { value: 'rsu_or_options',        label: 'Vested RSUs or exercised stock options', reportLabel: 'RSU / stock option event' },
      { value: 'none',                  label: 'None of these', reportLabel: 'None' },
    ],
  },

  // NEW — inline sub-question shown when 'retirement_withdrawal' is selected in major_events.
  // Roth is the calculator's own excluded/contested category (Spain doesn't clearly
  // recognize its US tax-free status) — traditional accounts are more settled.
  retirement_withdrawal_type: {
    key:        'retirement_withdrawal_type',
    storage:    'year',
    type:       'single_select',
    label:      'What type of account was this from?',
    shortLabel: 'Withdrawal account type',
    hint:       'Roth accounts have genuinely unsettled Spanish tax treatment.',
    options: [
      { value: 'traditional', label: 'Traditional (401(k), IRA, or pension)' },
      { value: 'roth',        label: 'Roth (401(k) or IRA)' },
      { value: 'not_sure',    label: 'Not sure' },
    ],
  },

  // ─── Filing history & goals ────────────────────────────────────────────────

  filed_spain_before: {
    key:        'filed_spain_before',
    storage:    'year',
    type:       'boolean',
    label:      'Have you filed a Spanish tax return before?',
    shortLabel: 'Filed Spain before',
    hint:       null,
    options: [
      { value: true,  label: 'Yes', reportLabel: 'Filed Spain taxes before' },
      { value: false, label: 'No — this would be my first time', reportLabel: 'No prior Spain filing' },
    ],
  },

  has_gestor: {
    key:        'has_gestor',
    storage:    'year',
    type:       'boolean',
    label:      'Do you currently work with a gestor or Spanish tax advisor?',
    shortLabel: 'Has gestor',
    hint:       null,
    options: [
      { value: true,  label: 'Yes, I have a gestor', reportLabel: 'Currently has gestor' },
      { value: false, label: 'No — I handle it myself or am looking for one', reportLabel: 'No current gestor' },
    ],
  },

  filing_goal: {
    key:        'filing_goal',
    storage:    'year',
    type:       'single_select',
    label:      "What's most pressing for you right now?",
    shortLabel: 'Filing goal',
    hint:       null,
    options: [
        // Full set — used by ProfilePage
        { value: 'file_overdue',           label: "I have an overdue return to file", reportLabel: 'Overdue return' },
        { value: 'file_upcoming',          label: "I need to file an upcoming return", reportLabel: 'Upcoming return' },
        { value: 'understand_obligations', label: "I want to understand what I owe", reportLabel: 'Understand obligations' },
        { value: 'find_advisor',           label: "I'm looking for a gestor or advisor", reportLabel: 'Seeking gestor / advisor' },
        { value: 'planning_ahead',         label: "I'm planning ahead for future years", reportLabel: 'Planning ahead' },
        { value: 'prepare_to_file',          label: 'Preparing to file' },
        { value: 'sanity_check',             label: 'Double-checking my situation', reportLabel: 'Sanity check / review' },
        { value: 'find_professional',        label: 'Looking for a professional', reportLabel: 'Seeking professional' },
        // ProfilePage legacy values
        { value: 'understand_requirements',  label: 'Understand my requirements' },
      ],
      triageOptions: ['file_overdue', 'file_upcoming', 'understand_obligations', 'find_advisor'],  },

}

// ─── Convenience helpers ───────────────────────────────────────────────────────

/**
 * Get the label for a given slot value.
 * Useful for display in case briefs, assessments, etc.
 *
 * getSlotLabel('citizenship_status', 'us_citizen') → '🇺🇸 US citizen (including dual citizens)'
 */
// Ticket 026: src/hooks/useCalculatorForm.js's own (independent, not-yet-fixed)
// COMUNIDAD_OPTIONS list emits 'castilla_mancha' where this registry's canonical
// value is 'castilla_la_mancha' — a single divergent key, not a wider mismatch.
// This alias is display-only: it does not change the stored value anywhere,
// it only lets lookups resolve the calculator's raw value to the correct label.
const COMUNIDAD_VALUE_ALIASES = {
  castilla_mancha: 'castilla_la_mancha',
}

function resolveComunidadAlias(slotKey, value) {
  return slotKey === 'comunidad_autonoma' && COMUNIDAD_VALUE_ALIASES[value]
    ? COMUNIDAD_VALUE_ALIASES[value]
    : value
}

export function getSlotLabel(slotKey, value) {
  const slot = slots[slotKey]
  if (!slot) return value
  const resolvedValue = resolveComunidadAlias(slotKey, value)
  const option = slot.options?.find(o => o.value === resolvedValue || String(o.value) === String(resolvedValue))
  return option?.label ?? value
}

/**
 * Get options for a slot, with optional label override.
 * Form configs can pass labelOverrides to customise copy without
 * touching canonical values.
 *
 * getSlotOptions('income_types', { rental: 'Rental income (Spain or abroad)' })
 */
/**
 * Get the professional/report-facing label for a slot value.
 * Falls back to getSlotLabel if no reportLabel is defined.
 * Use in briefs, assessments, and advisor-facing UI instead of getSlotLabel
 * where consumer-facing form copy would be inappropriate.
 *
 * getSlotReportLabel('filed_spain_before', false) → 'No prior Spain filing'
 * getSlotReportLabel('has_gestor', false)         → 'No current gestor'
 */
export function getSlotReportLabel(slotKey, value) {
  const slot = slots[slotKey]
  if (!slot) return String(value)
  const resolvedValue = resolveComunidadAlias(slotKey, value)
  const option = slot.options?.find(o => o.value === resolvedValue || String(o.value) === String(resolvedValue))
  return option?.reportLabel ?? option?.label ?? String(value)
}

export function getSlotOptions(slotKey, labelOverrides = {}, only = null) {
  const slot = slots[slotKey]
  if (!slot?.options) return []
  const filtered = only ? slot.options.filter(o => only.includes(o.value)) : slot.options
  return filtered.map(opt => ({
    ...opt,
    label: labelOverrides[opt.value] ?? opt.label,
  }))
}

/**
 * Normalize exclusive multi-select options.
 * When an exclusive value (e.g. 'none') is selected, deselect everything else.
 * When a non-exclusive value is selected, deselect any exclusive values.
 */
export function toggleMultiWithExclusive(current = [], value, exclusiveOptions = ['none']) {
  if (exclusiveOptions.includes(value)) {
    // Selecting exclusive clears everything else
    return current.includes(value) ? [] : [value]
  }
  // Selecting non-exclusive removes any exclusive values
  const without = current.filter(v => !exclusiveOptions.includes(v))
  return without.includes(value)
    ? without.filter(v => v !== value)
    : [...without, value]
}